import { useState } from 'react'
import toast from 'react-hot-toast'
import { Loader2, RotateCcw, Trash2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import ConfirmDialog from '@/components/common/ConfirmDialog'

/**
 * EMPEZAR DE NUEVO: vaciar la empresa de prueba y, si se quiere, volver a cargar todo.
 *
 * El primer intento de una migración casi nunca queda como se esperaba: precios con otra
 * columna, clientes duplicados en el sistema viejo, un catálogo a medias. Deshacer tipo por tipo
 * sirve para corregir UNO; cuando lo que hay que corregir es todo —o la empresa ya tenía
 * datos de ejemplo mezclados—, lo que el asesor quiere es dejarla vacía y cargar otra vez.
 *
 * Dos botones y no uno, porque son dos momentos: «vaciar y volver a cargar» cuando los archivos
 * ya están corregidos, y «solo vaciar» cuando todavía hay que ir a corregirlos.
 */
export default function EmpezarDeCero({ onPrevisualizar, onVaciar, enMarcha, hayQueCargar }) {
  const [preparando, setPreparando] = useState(null)
  const [confirmar, setConfirmar] = useState(null)
  const [trabajando, setTrabajando] = useState(false)

  // Primero se cuenta: el asesor confirma viendo cuántas filas se van.
  const pedir = async (recargar) => {
    setPreparando(recargar ? 'recargar' : 'vaciar')
    try {
      const d = await onPrevisualizar()
      if (!d) return
      const total = Number(d.previsualizacion?.total) || 0
      if (!recargar && total === 0) {
        toast('La empresa ya está vacía.')
        return
      }
      setConfirmar({ recargar, total })
    } finally {
      setPreparando(null)
    }
  }

  const ejecutar = async () => {
    setTrabajando(true)
    try {
      await onVaciar({ recargar: confirmar.recargar })
    } finally {
      setTrabajando(false)
      setConfirmar(null)
    }
  }

  const ocupado = enMarcha || preparando !== null || trabajando

  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div className="space-y-1">
          <p className="font-medium text-neutral-900 dark:text-neutral-50">Empezar de nuevo</p>
          <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
            Si lo que entró no es lo esperado, vacía la empresa y vuelve a cargar. Se borra toda la operación
            —lo importado, lo de ejemplo y lo tecleado— y se conservan la configuración, el emisor, los usuarios y
            el plan de cuentas. Los archivos y el mapeo de esta migración se quedan.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => pedir(true)} disabled={ocupado || !hayQueCargar}>
            {preparando === 'recargar' ? (
              <Loader2 size={15} className="mr-1.5 animate-spin" />
            ) : (
              <RotateCcw size={15} className="mr-1.5" />
            )}
            Vaciar y volver a cargar todo
          </Button>
          <Button variant="outline" onClick={() => pedir(false)} disabled={ocupado}>
            {preparando === 'vaciar' ? (
              <Loader2 size={15} className="mr-1.5 animate-spin" />
            ) : (
              <Trash2 size={15} className="mr-1.5" />
            )}
            Solo vaciar
          </Button>
        </div>
        {!hayQueCargar && (
          <p className="text-caption text-neutral-500 dark:text-neutral-400">
            Para volver a cargar hace falta al menos una hoja con su tipo de dato asignado.
          </p>
        )}
      </CardContent>

      <ConfirmDialog
        abierto={Boolean(confirmar)}
        onCerrar={() => !trabajando && setConfirmar(null)}
        onConfirmar={ejecutar}
        variante="destructiva"
        confirmacionEscrita="VACIAR"
        cargando={trabajando}
        titulo={confirmar?.recargar ? '¿Vaciar la empresa y volver a cargar?' : '¿Vaciar la empresa?'}
        descripcion={
          confirmar
            ? `Se borrarán ${confirmar.total.toLocaleString('es-SV')} fila(s) de operación. No se puede deshacer. ` +
              (confirmar.recargar
                ? 'Después se cargan otra vez, en orden, todos los tipos de dato de esta migración.'
                : 'Los tipos de dato quedan pendientes para cargarlos cuando quieras.')
            : ''
        }
        textoConfirmar={confirmar?.recargar ? 'Vaciar y cargar' : 'Vaciar la empresa'}
      />
    </Card>
  )
}
